"use client";
/* eslint-disable no-console */
import { useEffect, useRef, useState } from "react";

/** One entry in a GPT size mapping: a minimum viewport → sizes pair. */
export interface SizeMappingEntry {
  /** [minWidth, minHeight] of the viewport this rule applies to. */
  viewport: [number, number];
  /** Ad sizes to serve at this viewport. */
  sizes: number[][];
}

export interface AdUnitProps {
  /**
   * GAM ad unit path (e.g., "/789456/AB/web")
   */
  adUnitPath: string;
  /**
   * Ad sizes array (e.g., [[728, 90], [970, 250]])
   */
  sizes: number[][];
  /**
   * Element ID for the ad container
   */
  elementId: string;
  /**
   * GPT size mapping breakpoints.
   * When provided, GPT picks the matching sizes per viewport automatically.
   * Example: [{ viewport: [970, 0], sizes: [[970, 250]] }, { viewport: [0, 0], sizes: [[300, 250]] }]
   */
  sizeMapping?: SizeMappingEntry[];

  /**
   * Custom CSS styles for the ad container
   */
  style?: any;
  /**
   * CSS class name for the ad container
   */
  className?: string;
  /**
   * Load ads lazily when in viewport
   */
  lazy?: boolean;
}

declare global {
  interface Window {
    googletag: {
      cmd: Array<() => void>;
      defineSlot: (adUnitPath: string, sizes: number[][], elementId: string) => any;
      display: (elementId: string) => void;
      enableServices: () => void;
      destroySlots: (slots?: any[]) => void;
      pubads: () => {
        enableSingleRequest: () => void;
        collapseEmptyDivs: () => void;
        setTargeting: (key: string, value: string) => void;
      };
      sizeMapping: () => {
        addSize: (viewport: number[], sizes: number[][]) => any;
        build: () => any;
      };
    };
  }
}

const AdUnit = ({
  adUnitPath,
  sizes,
  elementId,
  style = {},
  className = "",
  sizeMapping,
}: AdUnitProps) => {
  const adRef = useRef<HTMLDivElement>(null);
  const isAdLoaded = useRef(false);
  const slotRef = useRef<any>(null);

  const [showFallback, setShowFallback] = useState(false);
  const [contentReady, setContentReady] = useState(false); // Wait for content first
  const [adReady, setAdReady] = useState(true); // Start with true - show ads by default

  // Wait for content to be ready before loading ads
  useEffect(() => {
    // Use a longer delay to ensure page content API calls complete first
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 3000); // Longer delay to ensure page data loads first

    return () => clearTimeout(timer);
  }, []);

  // Load GAM ads when visible and content ready
  useEffect(() => {
    if (typeof window === "undefined" || isAdLoaded.current || !contentReady) return;
    if (!window.googletag) {
      // Don't return - let's see if googletag loads later
    }

    // Wait a bit for googletag to be available
    const checkGoogleTag = () => {
      if (window.googletag) {
        const { googletag } = window;

        googletag.cmd.push(() => {
          try {
            googletag.defineSlot(adUnitPath, sizes, elementId)?.addService(googletag.pubads());
            const slot = googletag.defineSlot(adUnitPath, sizes, elementId);
            if (!slot) return; // already defined or invalid path
            slotRef.current = slot;
            slot.addService(googletag.pubads());
            // Apply responsive size mapping when provided so GPT selects
            // the right creative size per viewport (e.g. 300×250 on mobile,
            // 970×250 on desktop) without requiring a JS media-query check.
            if (sizeMapping && sizeMapping.length > 0) {
              const mappingBuilder = sizeMapping.reduce(
                (builder: any, entry: SizeMappingEntry) =>
                  builder.addSize(entry.viewport, entry.sizes),
                googletag.sizeMapping(),
              );
              slot.defineSizeMapping(mappingBuilder.build());
            }

            isAdLoaded.current = true;

            googletag.pubads().enableSingleRequest();
            googletag.enableServices();
            googletag.display(elementId);

            // Check for ad content after GAM loads
            setTimeout(() => {
              const adElement = document.getElementById(elementId);
              if (adElement) {
                const finalHeight = adElement.offsetHeight;
                const finalWidth = adElement.offsetWidth;
                const hasIframe = adElement.querySelector("iframe");
                // More permissive - show ads unless clearly collapsed (0x0)
                if (finalHeight === 0 && finalWidth === 0 && !hasIframe) {
                  setAdReady(false);
                } else {
                  setAdReady(true);
                }
              } else {
                setAdReady(true); // Show by default if element exists
              }
            }, 1000);
          } catch (error) {
            console.error("GAM AdUnit error:", error);
            setShowFallback(true);
          }
        });
      } else {
        console.log("GAM: googletag still not available after wait");
      }
    };

    // Try immediately and also after a shorter delay
    checkGoogleTag();
    const timer = setTimeout(checkGoogleTag, 500); // Reduced from 1000ms

    return () => {
      clearTimeout(timer);
      if (window.googletag && isAdLoaded.current) {
        window.googletag.cmd.push(() => {
          try {
            console.log("GAM: Cleanup called");
          } catch (error) {
            console.error("GAM AdUnit cleanup error:", error);
          }
        });
      }
    };
  }, [adUnitPath, sizes, elementId, contentReady]); // Wait for content ready

  const shouldShowAds = contentReady && adReady;

  const [defaultWidth = 320] = sizes[0] || [];
  // When sizeMapping is active the browser viewport determines which creative
  // size GPT serves, so we let the outer container control the width instead
  // of locking to the first entry in the sizes array.
  const containerWidth = sizeMapping && sizeMapping.length > 0 ? "100%" : defaultWidth;

  const defaultStyle: any = {
    display: shouldShowAds ? "block" : "none",
    width: containerWidth,
    margin: "10px auto",
    textAlign: "center" as const,
    ...style,
  };

  // Don't render anything until content is ready
  if (!contentReady) {
    return null;
  }

  return (
    <div
      ref={adRef}
      style={defaultStyle}
      className={`gam-ad-container ${className || ""}`}
      data-testid='gam-container'
      data-status={showFallback ? "failed" : adReady ? "loaded" : "loading"}>
      <div id={elementId} className='gam-ad-slot' />
    </div>
  );
};

export default AdUnit;
